import Alpine from "alpinejs";
window.Alpine = Alpine;

// Global cookie consent store - defined immediately so it's available when Alpine starts
Alpine.store('consent', {
    list: [],
    has(requirements = []) {
        return requirements.every(req => this.list.includes(req));
    },
    allow(consents) {
        consents.forEach((consent) => {
            if (!this.list.includes(consent)) {
                this.list.push(consent);
            }
        });
    }
});

document.addEventListener('alpine:init', () => {
    // Button group with a "show more" button: the buttons after it are rendered but hidden
    // (Partials/fluid_styled_content/Buttons/Layout-1.html, button style "show-more")
    Alpine.data('showMoreGroup', () => ({
        moreShown: false,
    }));

    // Lazy video loading - loads video when it enters viewport
    Alpine.data('lazyVideo', () => ({
        _observer: null,
        observe() {
            if (!('IntersectionObserver' in window)) {
                // Fallback: load immediately if IntersectionObserver not supported
                this.load();
                return;
            }
            this._observer = new IntersectionObserver((entries, obs) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.load();
                        obs.unobserve(entry.target);
                    }
                });
            }, {
                rootMargin: '200px 0px' // Start loading 200px before entering viewport
            });
            this._observer.observe(this.$el);
        },
        load() {
            const video = this.$el;
            video.preload = 'metadata';
            video.load();
        },
        destroy() {
            if (this._observer) {
                this._observer.disconnect();
            }
        }
    }));

    // Play on hover (desktop) / play in viewport (mobile)
    Alpine.data('hoverVideo', () => ({
        _observer: null,
        _isMobile: false,
        _isLoaded: false,
        init() {
            // Detect mobile/touch device
            this._isMobile = window.matchMedia('(max-width: 1024px)').matches ||
                             'ontouchstart' in window ||
                             navigator.maxTouchPoints > 0;

            const video = this.$el;
            video.preload = 'metadata';

            if (this._isMobile) {
                this._setupMobileObserver();
            }
        },
        _setupMobileObserver() {
            if (!('IntersectionObserver' in window)) {
                return;
            }
            this._observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this._loadAndPlay();
                    } else {
                        this.pause();
                    }
                });
            }, {
                threshold: 0.5 // Play when 50% visible
            });
            this._observer.observe(this.$el);
        },
        _loadAndPlay() {
            const video = this.$el;
            if (!this._isLoaded) {
                video.load();
                this._isLoaded = true;
            }
            video.play().catch(() => {
                // Autoplay blocked - silently fail
            });
        },
        play() {
            if (this._isMobile) return; // Mobile uses observer
            this._loadAndPlay();
        },
        pause() {
            const video = this.$el;
            if (!video.paused) {
                video.pause();
            }
        },
        destroy() {
            if (this._observer) {
                this._observer.disconnect();
            }
        }
    }));

    Alpine.data('page', () => ({
        // state
        isOpenMobileMenu: false,
        overflowHidden: false,
        openMenuPages: [],
        consents: [],

        // ==== INIT: lazy background loader ====
        init() {
            const root = this.$root || document;
            const lazyBackgroundImages = Array.from(root.querySelectorAll('.lazyloading-bg'));

            if ('IntersectionObserver' in window) {
                const observer = new IntersectionObserver((entries, obs) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            // once visible, drop the blocking class so the bg-image can apply
                            entry.target.classList.remove('lazyloading-bg');
                            obs.unobserve(entry.target);
                        }
                    });
                }, {
                    // start a bit early so images are ready when you scroll to them
                    rootMargin: '200px 0px'
                });

                lazyBackgroundImages.forEach((el) => observer.observe(el));
                // keep a ref if you ever want to disconnect manually
                this._lazyBgObserver = observer;
            } else {
                // Fallback: no IO support → just load immediately
                lazyBackgroundImages.forEach((el) => el.classList.remove('lazyloading-bg'));
            }

            this.initRteFoldouts();
            this.scrollToInitialAnchor();
        },
        focusContactStickyButton() {
            document.querySelector('#contact a:first-child')?.focus();
        },
        focusParentStickyButton(el) {
            el.parentElement.focus();
        },
        scrollToInitialAnchor() {
            const hash = window.location.hash;
            if (!hash || hash.length < 2) {
                return;
            }

            const raw = hash.slice(1);
            const decoded = decodeURIComponent(raw);
            const cssEscape = (value) => {
                if (window.CSS && typeof window.CSS.escape === 'function') {
                    return window.CSS.escape(value);
                }
                return value.replace(/["\\]/g, '\\$&');
            };

            const target =
                document.getElementById(decoded) ||
                document.querySelector(`[name="${cssEscape(decoded)}"]`);

            if (!target) {
                return;
            }

            const prefersReduced = window.matchMedia &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            const performScroll = () => {
                try {
                    target.scrollIntoView({
                        behavior: prefersReduced ? 'auto' : 'smooth',
                        block: 'start'
                    });
                } catch (err) {
                    target.scrollIntoView();
                }
            };

            requestAnimationFrame(() => {
                setTimeout(performScroll, 0);
            });
        },
        initRteFoldouts() {
            const root = this.$root || document.body;
            if (!root) {
                return;
            }

            const openLabel = root.dataset.rteFoldoutOpen || 'Show more';
            const closeLabel = root.dataset.rteFoldoutClose || 'Show less';

            const markers = Array.from(root.querySelectorAll('.fold-break, .page-break'));
            markers.forEach((marker) => {
                const parent = marker.parentNode;
                if (!parent || parent.nodeType !== 1) {
                    return;
                }

                const foldout = document.createElement('div');
                foldout.className = 'rte-foldout';
                foldout.dataset.foldOpenLabel = marker.dataset.foldOpen || '';
                foldout.dataset.foldCloseLabel = marker.dataset.foldClose || '';

                let sibling = marker.nextSibling;
                while (sibling) {
                    const nextSibling = sibling.nextSibling;
                    foldout.appendChild(sibling);
                    sibling = nextSibling;
                }

                const foldoutContainer = document.createElement('div');
                foldoutContainer.className = 'rte-foldout-container';
                const foldoutInner = document.createElement('div');
                foldoutInner.className = 'rte-foldout-inner';
                while (foldout.firstChild) {
                    foldoutInner.appendChild(foldout.firstChild);
                }
                foldoutContainer.appendChild(foldoutInner);
                foldout.appendChild(foldoutContainer);

                parent.insertBefore(foldout, marker.nextSibling);
                marker.remove();
            });

            root.querySelectorAll('.rte-foldout').forEach((foldout) => {
                if (foldout.dataset.rteFoldoutInitialized) {
                    return;
                }

                const buttonWrap = document.createElement('div');
                buttonWrap.className = 'rte-foldout-toggle-wrap';
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'button-ghost-tiny color-1';
                buttonWrap.appendChild(button);

                const bottomButtonWrap = document.createElement('div');
                bottomButtonWrap.className = 'rte-foldout-toggle-wrap rte-foldout-toggle-wrap--bottom';
                const bottomButton = document.createElement('button');
                bottomButton.type = 'button';
                bottomButton.className = 'button-ghost-tiny color-1';
                bottomButtonWrap.appendChild(bottomButton);

                const foldoutOpenLabel = foldout.dataset.foldOpenLabel || openLabel;
                const foldoutCloseLabel = foldout.dataset.foldCloseLabel || closeLabel;

                const setState = (isOpen) => {
                    foldout.classList.toggle('is-open', isOpen);
                    button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
                    bottomButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
                    button.textContent = isOpen ? foldoutCloseLabel : foldoutOpenLabel;
                    bottomButton.textContent = foldoutCloseLabel;
                    bottomButtonWrap.hidden = !isOpen;
                    buttonWrap.hidden = isOpen;
                };

                setState(false);
                const toggle = () => setState(!foldout.classList.contains('is-open'));
                button.addEventListener('click', toggle);
                bottomButton.addEventListener('click', toggle);

                foldout.dataset.rteFoldoutInitialized = '1';
                foldout.parentNode.insertBefore(buttonWrap, foldout);
                foldout.parentNode.insertBefore(bottomButtonWrap, foldout.nextSibling);
            });
        },
        hasConsent(requirements = []) {
            return Alpine.store('consent').has(requirements);
        },
        allowConsents(consents) {
            Alpine.store('consent').allow(consents);
        },
        // ==== mobile menu ====
        toggleMobileMenu() {
            if (this.isOpenMobileMenu) {
                this.closeMobileMenu();
            } else {
                this.openMobileMenu();
            }
        },
        closeMobileMenu() {
            this.isOpenMobileMenu = false;
            this.overflowHidden = false;
            this.closeAllMenues();
        },
        openMobileMenu() {
            this.isOpenMobileMenu = true;
            this.overflowHidden = true;
        },

        // ==== subpages ====
        toggleMenuSubpage(id) {
            if (this.isMenuOpen(id)) {
                this.closeMenuSubpage(id);
            } else {
                this.openMenuSubpage(id);
            }
        },
        openMenuSubpage(id) {
            this.openMenuPages.push(id);
        },
        closeMenuSubpage(id) {
            this.openMenuPages = this.openMenuPages.filter((item) => item !== id);
        },
        closeAllMenues() {
            this.openMenuPages = [];
        },
        isMenuOpen(id) {
            return this.openMenuPages.includes(id);
        },
    }));

    Alpine.data('desktopMenuItem', () => ({
        open: false,
        toggleMenu() { this.open = !this.open },
        closeMenu()  { this.open = false },
        openMenu()   { this.open = true }
    }));

    // Header2 component - two-tier header with scroll effects
    Alpine.data('header2', () => ({
        scrolled: false,
        topBarVisible: true,
        hidden: false,
        mobileMenuOpen: false,
        openMenuPages: [],
        _lastScrollY: 0,
        _scrollThreshold: 60, // min scroll distance before hiding/showing

        init() {
            this._lastScrollY = window.scrollY;
            this.handleScroll();
            window.addEventListener('scroll', () => this.handleScroll(), { passive: true });
        },

        handleScroll() {
            const scrollY = window.scrollY;
            this.scrolled = scrollY > 50;
            this.topBarVisible = scrollY < 100;

            // Hide on scroll down, show on scroll up
            const diff = scrollY - this._lastScrollY;

            if (Math.abs(diff) > this._scrollThreshold) {
                // Scrolling down & past the header height -> hide
                if (diff > 0 && scrollY > 100) {
                    this.hidden = true;
                }
                // Scrolling up -> show
                else if (diff < 0) {
                    this.hidden = false;
                }
                this._lastScrollY = scrollY;
            }

            // Always show when at the top
            if (scrollY < 50) {
                this.hidden = false;
            }
        },

        toggleMobileMenu() {
            this.mobileMenuOpen = !this.mobileMenuOpen;
            document.body.style.overflow = this.mobileMenuOpen ? 'hidden' : '';
        },

        closeMobileMenu() {
            this.mobileMenuOpen = false;
            document.body.style.overflow = '';
            this.closeAllMenues();
        },

        toggleMenuSubpage(id) {
            if (this.isMenuOpen(id)) {
                this.closeMenuSubpage(id);
            } else {
                this.openMenuSubpage(id);
            }
        },

        openMenuSubpage(id) {
            this.openMenuPages.push(id);
        },

        closeMenuSubpage(id) {
            this.openMenuPages = this.openMenuPages.filter((item) => item !== id);
        },

        closeAllMenues() {
            this.openMenuPages = [];
        },

        isMenuOpen(id) {
            return this.openMenuPages.includes(id);
        }
    }));

    // Newsletter subscription component
    Alpine.data('newsletter', () => ({
        email: '',
        status: 'idle', // idle, loading, success, error
        errorMessage: '',

        async subscribe() {
            if (!this.email || this.status === 'loading') return;

            this.status = 'loading';
            this.errorMessage = '';

            try {
                const form = this.$el.querySelector('form');
                const action = form.getAttribute(':action')?.replace(/'/g, '') || form.action;

                if (!action) {
                    // If no action defined, just show success for demo
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    this.status = 'success';
                    return;
                }

                const response = await fetch(action, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams({ email: this.email })
                });

                if (response.ok) {
                    this.status = 'success';
                    this.email = '';
                } else {
                    throw new Error('Subscription failed');
                }
            } catch (error) {
                this.status = 'error';
                this.errorMessage = error.message || 'An error occurred. Please try again.';
                setTimeout(() => {
                    this.status = 'idle';
                }, 3000);
            }
        }
    }));

    // AJAX Form submission with animations and client-side validation
    Alpine.data('ajaxForm', (config = {}) => ({
        status: 'idle', // idle, submitting, success, error
        errorMessage: '',
        successMessage: '',
        fieldErrors: {},
        messages: {
            required: 'This field must be filled!',
            email: 'This is not a valid email address!',
            minlength: 'Please enter at least %s characters.',
            select: 'Please select an option!',
            checkbox: 'This field is required!',
            ...(config.messages || {})
        },

        init() {
            console.log('[ajaxForm] Initializing validation');
            this.$nextTick(() => {
                this._initValidation();
            });
        },

        _initValidation() {
            const form = this.$el.querySelector('form');
            if (!form) {
                console.log('[ajaxForm] No form found');
                return;
            }

            // Get all form fields
            const fields = form.querySelectorAll('input:not([type="hidden"]):not([type="submit"]), select, textarea');

            fields.forEach((field) => {
                // Skip if already has listeners (prevents duplicate listeners on re-init)
                if (field.dataset.validationInit) return;
                field.dataset.validationInit = 'true';

                // Add validation on input - only if field already has an error (real-time feedback while fixing)
                field.addEventListener('input', () => {
                    const fieldId = field.id || field.name;
                    if (this.fieldErrors[fieldId]) {
                        this._validateField(field);
                    }
                });

                // Add validation on change (for selects and checkboxes) - always validate
                field.addEventListener('change', () => this._validateField(field));

                // Add validation on blur (when leaving field) - always validate
                field.addEventListener('blur', () => this._validateField(field));
            });

            console.log('[ajaxForm] Validation initialized for', fields.length, 'fields');
        },

        _validateField(field) {
            const fieldId = field.id || field.name;
            const fieldType = field.type;
            const isRequired = field.hasAttribute('required') || field.getAttribute('aria-required') === 'true';
            const minLength = field.getAttribute('minlength');

            // Get custom messages from data attributes (fallback to defaults)
            const customMessages = {
                required: field.dataset.msgRequired || this.messages.required,
                email: field.dataset.msgEmail || this.messages.email,
                minlength: field.dataset.msgMinlength || this.messages.minlength,
                select: field.dataset.msgSelect || this.messages.select,
                checkbox: field.dataset.msgCheckbox || this.messages.checkbox
            };

            let value = field.value;
            let errorMessage = '';

            // Trim whitespace for validation (except passwords)
            if (fieldType !== 'password') {
                value = value.trim();
            }

            // Required field validation
            if (isRequired) {
                if (fieldType === 'checkbox') {
                    if (!field.checked) {
                        errorMessage = customMessages.checkbox;
                    }
                } else if (fieldType === 'select-one' || field.tagName === 'SELECT') {
                    if (!value || value === '') {
                        errorMessage = customMessages.select;
                    }
                } else if (!value) {
                    errorMessage = customMessages.required;
                }
            }

            // Email validation (only if field has value and is email type)
            if (!errorMessage && value && (fieldType === 'email' || field.name.toLowerCase().includes('email'))) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) {
                    errorMessage = customMessages.email;
                }
            }

            // Minlength validation
            if (!errorMessage && value && minLength && value.length < parseInt(minLength, 10)) {
                errorMessage = customMessages.minlength.replace('%s', minLength);
            }

            // Update field errors
            if (errorMessage) {
                this.fieldErrors[fieldId] = errorMessage;
                this._showFieldError(field, errorMessage);
            } else {
                delete this.fieldErrors[fieldId];
                this._hideFieldError(field);
            }

            return !errorMessage;
        },

        _getFieldWrapper(field) {
            // For checkboxes, find the parent with has-error potential
            if (field.type === 'checkbox') {
                // Checkbox structure: div.flex.flex-col > div.flex.items-start > input
                return field.closest('.flex.flex-col') ||
                       field.closest('fieldset') ||
                       field.parentElement.parentElement;
            }

            // For regular fields wrapped in Field.html
            // Structure: div.flex.flex-col > ... > div (container) > input
            return field.closest('.flex.flex-col') || field.parentElement;
        },

        _getErrorInsertionPoint(field, wrapper) {
            // For checkboxes, insert after the flex wrapper (checkbox + label container)
            if (field.type === 'checkbox') {
                return field.closest('.flex.items-start');
            }

            // For regular inputs inside Field/Field.html structure
            // Insert after the inner container div
            const innerContainer = wrapper.querySelector('div:has(> input, > select, > textarea)') ||
                                   wrapper.querySelector('div:nth-child(2)');
            return innerContainer || field.parentElement;
        },

        _showFieldError(field, message) {
            const wrapper = this._getFieldWrapper(field);

            // Add error styling to field
            field.classList.add('border-red-600');
            if (field.type !== 'checkbox') {
                field.classList.add('border-2');
                field.classList.remove('border-current');
            }
            field.setAttribute('aria-invalid', 'true');

            // Add has-error class to wrapper
            if (wrapper) {
                wrapper.classList.add('has-error');
            }

            // Find or create error message element
            const errorId = `${field.id || field.name}-client-error`;
            let errorEl = wrapper ? wrapper.querySelector(`#${CSS.escape(errorId)}`) : null;

            if (!errorEl && wrapper) {
                // Check if there's already a server-side error element
                const serverError = wrapper.querySelector('[role="alert"]:not([id$="-client-error"])');
                if (serverError) {
                    // Hide server error when showing client error
                    serverError.style.display = 'none';
                }

                errorEl = document.createElement('div');
                errorEl.id = errorId;
                errorEl.className = 'text-red-600 text-[length:var(--font-size-sm)] mt-1';
                errorEl.setAttribute('role', 'alert');
                errorEl.setAttribute('aria-live', 'polite');

                // Find the right insertion point
                const insertionPoint = this._getErrorInsertionPoint(field, wrapper);
                if (insertionPoint) {
                    insertionPoint.insertAdjacentElement('afterend', errorEl);
                } else {
                    wrapper.appendChild(errorEl);
                }
            }

            if (errorEl) {
                errorEl.textContent = message;
                field.setAttribute('aria-describedby', errorId);
            }
        },

        _hideFieldError(field) {
            const wrapper = this._getFieldWrapper(field);

            // Remove error styling from field
            field.classList.remove('border-red-600');
            if (field.type !== 'checkbox') {
                field.classList.remove('border-2');
                field.classList.add('border-current');
            }
            field.setAttribute('aria-invalid', 'false');

            // Remove has-error class
            if (wrapper) {
                wrapper.classList.remove('has-error');
            }

            // Remove error message element
            const errorId = `${field.id || field.name}-client-error`;
            const errorEl = wrapper ? wrapper.querySelector(`#${CSS.escape(errorId)}`) : null;
            if (errorEl) {
                errorEl.remove();
            }

            // Show server error again if it exists
            if (wrapper) {
                const serverError = wrapper.querySelector('[role="alert"]:not([id$="-client-error"])');
                if (serverError) {
                    serverError.style.display = '';
                }
            }

            // Clean up aria-describedby
            const describedBy = field.getAttribute('aria-describedby');
            if (describedBy === errorId) {
                field.removeAttribute('aria-describedby');
            }
        },

        _validateAllFields() {
            const form = this.$el.querySelector('form');
            if (!form) return true;

            const fields = form.querySelectorAll('input:not([type="hidden"]):not([type="submit"]), select, textarea');
            let isValid = true;

            fields.forEach((field) => {
                if (!this._validateField(field)) {
                    isValid = false;
                }
            });

            return isValid;
        },

        async submit(event) {
            if (this.status === 'submitting') return;

            // Validate all fields before submitting
            if (!this._validateAllFields()) {
                console.log('[ajaxForm] Validation failed, not submitting');
                // Focus first error field
                const firstErrorField = this.$el.querySelector('[aria-invalid="true"]');
                if (firstErrorField) {
                    firstErrorField.focus();
                    firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                return;
            }

            const form = event.target;
            const formId = form.id;
            this.status = 'submitting';
            this.errorMessage = '';

            console.log('[ajaxForm] Submitting form:', formId);

            try {
                const formData = new FormData(form);
                const response = await fetch(form.action, {
                    method: 'POST',
                    body: formData,
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                });

                const html = await response.text();
                console.log('[ajaxForm] Response received, length:', html.length);

                // Parse response HTML
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');

                // Check for validation errors (has-error class on form fields)
                const hasValidationErrors = doc.querySelector('.has-error');

                // Check if response still contains the same form (with form fields)
                const responseForm = doc.querySelector(`#${formId}, form[id="${formId}"]`);
                const hasFormFields = responseForm && responseForm.querySelector('input:not([type="hidden"]), select, textarea');

                // Check for confirmation/success indicators
                // - No form with input fields in response = success (Confirmation finisher)
                // - Or explicit confirmation class
                const hasConfirmation = doc.querySelector('.fullhd-form-confirmation, .frenzelit-form-confirmation, .frame-type-form_formframework .confirmation');
                const isSuccess = hasConfirmation || (responseForm && !hasFormFields && !hasValidationErrors);

                console.log('[ajaxForm] hasValidationErrors:', !!hasValidationErrors, 'hasFormFields:', !!hasFormFields, 'isSuccess:', isSuccess);

                if (isSuccess) {
                    // Success - show our local confirmation
                    this.status = 'success';
                    console.log('[ajaxForm] Form submitted successfully');

                    // Scroll to top of form wrapper smoothly
                    this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } else if (hasValidationErrors) {
                    // Validation errors - update form content
                    console.log('[ajaxForm] Validation errors found');
                    if (responseForm) {
                        const formContent = form.querySelector('.frenzelit-form-content');
                        const newFormContent = responseForm.querySelector('.frenzelit-form-content');
                        if (formContent && newFormContent) {
                            formContent.innerHTML = newFormContent.innerHTML;
                        }
                    }
                    this.status = 'idle';
                    // Re-initialize validation for updated form
                    this.$nextTick(() => this._initValidation());

                    // Focus first error field
                    this.$nextTick(() => {
                        const firstError = this.$el.querySelector('.has-error input, .has-error select, .has-error textarea');
                        if (firstError) {
                            firstError.focus();
                            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    });
                } else if (responseForm) {
                    // Form still present but no errors - possibly multi-step, update content
                    console.log('[ajaxForm] Multi-step form, updating content');
                    const formContent = form.querySelector('.frenzelit-form-content');
                    const newFormContent = responseForm.querySelector('.frenzelit-form-content');
                    if (formContent && newFormContent) {
                        formContent.innerHTML = newFormContent.innerHTML;
                    }
                    this.status = 'idle';
                    // Re-initialize validation for updated form
                    this.$nextTick(() => this._initValidation());
                } else {
                    // No form in response and no confirmation - treat as success
                    console.log('[ajaxForm] No form in response, treating as success');
                    this.status = 'success';
                    this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            } catch (error) {
                console.error('[ajaxForm] Form submission error:', error);
                this.status = 'error';
                this.errorMessage = 'An error occurred. Please try again.';

                // Reset after delay
                setTimeout(() => {
                    if (this.status === 'error') {
                        this.status = 'idle';
                    }
                }, 5000);
            }
        },

        reset() {
            this.status = 'idle';
            this.errorMessage = '';
            this.successMessage = '';
            this.fieldErrors = {};

            // Clear all field error displays
            const form = this.$el.querySelector('form');
            if (form) {
                const fields = form.querySelectorAll('[aria-invalid="true"]');
                fields.forEach((field) => this._hideFieldError(field));
            }
        }
    }));

    // Fade in on scroll animation using IntersectionObserver
    Alpine.data('fadeInOnView', () => ({
        visible: false,
        _observer: null,

        init() {
            if (!('IntersectionObserver' in window)) {
                // Fallback: show immediately if IntersectionObserver not supported
                this.visible = true;
                return;
            }

            this._observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.visible = true;
                        // Once visible, stop observing to prevent repeated animations
                        this._observer.unobserve(entry.target);
                    }
                });
            }, {
                threshold: 0.2, // Trigger when 20% visible
                rootMargin: '0px 0px -50px 0px' // Slight offset from bottom
            });

            this._observer.observe(this.$el);
        },

        destroy() {
            if (this._observer) {
                this._observer.disconnect();
            }
        },

        show() {
            this.visible = true;
        },

        hide() {
            this.visible = false;
        }
    }));
})

if (document.readyState === 'complete') {
    Alpine.start();
} else {
    window.addEventListener('load', () => Alpine.start(), { once: true });
}
